import { IAccount } from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../../../components/Tabs';
import { EyeIcon } from '../../../../components/svgs/icons/EyeIcon';
import { ListOfQuestionsIcon } from '../../../../components/svgs/icons/ListOfQuestionsIcon';
import { TabSettingsIcon } from '../../../../components/svgs/icons/TabSettingsIcon';
import { usePreviewMode } from '../../shared/contexts/PreviewModeContext';
import { AdditionalQuestions } from '../../shared/components/AdditionalQuestions';
import { FieldStateManager } from '../../shared/components/AdditionalQuestions/types';
import { ReorderQuestionsButton } from '../../shared/components/ReorderQuestionsButton';
import { SurveySettings } from '../../shared/components/SurveySettings';
import { styleSheet } from '../styles';
import { TabValues } from '../utils';
import { BasicInfoSettings } from './BasicInfoSettings';
import { ConfirmationSettings } from './ConfirmationSettings';
import { DateRangeSettings } from './DateRangeSettings';
import { PreviewCustomSurveyForm } from './PreviewCustomSurveyForm';

interface SurveyTabsProps {
	selectedTab: TabValues;
	onTabChange: (value: TabValues) => void;
	settingsTabError: boolean;
	questionsTabError: boolean;
	surveyData?: any;
	account?: IAccount;
	isEdit: boolean;
	isClone: boolean;
	onReorderQuestions?: () => void;
	hasMultipleQuestions?: boolean;
	isLoading?: boolean;
	templateMode?: boolean;
	/** Field IDs whose conditional visibility was cleared by reordering */
	clearedVisibilityFieldIds?: string[];
	/** Field IDs that should be marked dirty due to reordering (position change or cleared visibility) */
	reorderDirtyFieldIds?: string[];
	/** Lifted per-field dirty manager so the container's save mutation can clear it after success */
	fieldStateManager?: FieldStateManager;
}

export const SurveyTabs: React.FC<SurveyTabsProps> = ({
	selectedTab,
	onTabChange,
	settingsTabError,
	questionsTabError,
	surveyData,
	account,
	isEdit,
	isClone,
	onReorderQuestions,
	hasMultipleQuestions = false,
	isLoading = false,
	templateMode,
	clearedVisibilityFieldIds,
	reorderDirtyFieldIds,
	fieldStateManager,
}) => {
	// Get preview mode from context
	const { isPreviewOnly } = usePreviewMode();
	return (
		<TabRoot
			className={`tab-view ${css(styleSheet.tabViewContainer)}`}
			value={selectedTab}
			onValueChange={tValue => onTabChange(tValue as TabValues)}
		>
			<TabList styles={[styleSheet.tabList]}>
				<IconTabTrigger
					value={TabValues.Settings}
					icon={<TabSettingsIcon fillColor='currentColor' />}
					styles={settingsTabError ? [styleSheet.tabTextError] : []}
				>
					Settings
				</IconTabTrigger>
				<IconTabTrigger
					value={TabValues.Questions}
					icon={<ListOfQuestionsIcon fillColor='currentColor' />}
					styles={questionsTabError ? [styleSheet.tabTextError] : []}
				>
					Questions
				</IconTabTrigger>
				{surveyData?.anonymousLink || isPreviewOnly ? (
					<IconTabTrigger value={TabValues.Preview} icon={<EyeIcon fillColor='currentColor' />}>
						Preview
					</IconTabTrigger>
				) : null}
			</TabList>
			<TabContent
				forceMount
				value={TabValues.Settings}
				className={css(
					styleSheet.tabContent,
					styleSheet.newTabContent,
					selectedTab !== TabValues.Settings && styleSheet.tabContentHidden
				)}
			>
				<SurveySettings
					tabs={[
						{
							title: 'Basic Info',
							key: 'contact-fields',
							content: <BasicInfoSettings survey={surveyData} account={account} />,
						},
						{
							title: 'Date Range',
							key: 'date-range',
							content: <DateRangeSettings survey={surveyData} />,
						},
						{
							title: 'Confirmation',
							key: 'confirmation',
							content: <ConfirmationSettings survey={surveyData} />,
						},
					]}
				/>
			</TabContent>
			<TabContent
				forceMount
				value={TabValues.Questions}
				className={`scroll-view-items ${css(selectedTab !== TabValues.Questions && styleSheet.tabContentHidden)}`}
			>
				<div className={css(styleSheet.formContainer)}>
					<div className={css(styleSheet.bodyBackground)} />
					{onReorderQuestions && !isPreviewOnly && (
						<ReorderQuestionsButton
							onReorderQuestions={onReorderQuestions}
							hasMultipleQuestions={hasMultipleQuestions}
							isLoading={isLoading}
						/>
					)}
					<div className={css(styleSheet.additionalQuestionsContainer)}>
						<AdditionalQuestions
							isEdit={isEdit}
							isClone={isClone}
							templateMode={templateMode}
							fieldStateManager={fieldStateManager}
							clearedVisibilityFieldIds={clearedVisibilityFieldIds}
							reorderDirtyFieldIds={reorderDirtyFieldIds}
						/>
					</div>
				</div>
			</TabContent>
			{surveyData?.anonymousLink || isPreviewOnly ? (
				<TabContent value={TabValues.Preview} className={css(styleSheet.tabContent)}>
					<div className={css(styleSheet.formContainer)}>
						<PreviewCustomSurveyForm survey={surveyData} account={account} />
					</div>
				</TabContent>
			) : null}
		</TabRoot>
	);
};
